import styled from "@emotion/styled";
import { BorderOutlined, FileTextOutlined, WarningOutlined } from "@ant-design/icons";
import { Button, Col, Row } from "antd";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "store";
import { setTabSelected } from "store/slices/orderSlice";
import { paths } from "setup/PageRouter/routesConfig";
import { formatUpdatedAt } from "utils/thaiDate";
import { useSellerTodoSummary } from "hooks/useSellerReport";
import { sd } from "../tokens";
import TodoTile from "./TodoTile";

/** แผง "สิ่งที่ต้องทำวันนี้" — ตัวเลขเป็นยอดสด ไม่ขึ้นกับช่วงเวลาที่เลือกด้านบน */
const TodoPanel = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data, isPending, isError, dataUpdatedAt, refetch } = useSellerTodoSummary();

  const goToNewOrders = () => {
    // OrderManage อ่านแท็บที่เปิดอยู่จาก redux — ตั้งไปที่ "รับคำสั่งซื้อ" ก่อนแล้วค่อย navigate
    dispatch(setTabSelected("0"));
    navigate(paths.order);
  };

  return (
    <Panel>
      <PanelHead>
        <div>
          <Eyebrow>สิ่งที่ต้องทำวันนี้</Eyebrow>
          <Title>จัดการร้านให้เรียบร้อย</Title>
        </div>
        <Status>
          {isError ? (
            <>
              <ErrorDot />
              <span>โหลดไม่สำเร็จ</span>
              <Button size="small" type="link" onClick={() => refetch()} style={{ paddingInline: 4 }}>
                ลองใหม่
              </Button>
            </>
          ) : (
            <>
              <LiveDot />
              <span>{formatUpdatedAt(dataUpdatedAt)}</span>
            </>
          )}
        </Status>
      </PanelHead>

      <Row gutter={[12, 12]}>
        <Col xs={24} sm={24} md={8}>
          <TodoTile
            loading={isPending}
            count={data?.new_orders_to_prepare ?? 0}
            label="ออเดอร์ใหม่รอเตรียมสินค้า"
            hint="กดเพื่อไปหน้าคำสั่งซื้อ"
            icon={<BorderOutlined />}
            iconColor={sd.orange}
            onClick={goToNewOrders}
          />
        </Col>
        <Col xs={24} sm={24} md={8}>
          <TodoTile
            loading={isPending}
            count={data?.parcels_awaiting_pickup ?? 0}
            label="พัสดุรอขนส่งเข้ารับ"
            hint="กดเพื่อไปหน้าการจัดส่ง"
            icon={<FileTextOutlined />}
            iconColor={sd.blue}
            onClick={() => navigate(paths.shipping)}
          />
        </Col>
        <Col xs={24} sm={24} md={8}>
          <TodoTile
            loading={isPending}
            count={data?.low_stock_products ?? 0}
            label="สินค้าใกล้หมดสต็อก"
            hint="กดเพื่อไปหน้าสินค้าและสต็อก"
            icon={<WarningOutlined />}
            iconColor={sd.red}
            onClick={() => navigate(paths.manageProduct)}
          />
        </Col>
      </Row>
    </Panel>
  );
};

export default TodoPanel;

const Panel = styled.div`
  background: ${sd.navy};
  border-radius: ${sd.radius}px;
  padding: 20px;
  color: #fff;
`;

const PanelHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const Eyebrow = styled.div`
  font-size: 12px;
  color: #9FB0CC;
  margin-bottom: 2px;
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: 700;
`;

const Status = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #9FB0CC;
`;

const LiveDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${sd.green};
`;

const ErrorDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${sd.red};
`;
